import React, {Fragment, useEffect, useState} from 'react'
import {Dialog, Menu, Transition} from '@headlessui/react'
import {
    Bars3Icon,
    Cog6ToothIcon,
    CreditCardIcon,
    CurrencyDollarIcon,
    HomeIcon,
    PresentationChartLineIcon,
    XMarkIcon
} from '@heroicons/react/24/outline'
import {ChevronDownIcon} from '@heroicons/react/20/solid'
import Logo from "../components/Logo";
import useAuthStore from "../store/auth";
import {Link, useLocation, useNavigate} from "react-router-dom";
import Language from "../components/Language";
import {useTranslation} from "react-i18next";
import {clsx} from "clsx";
import MerchantRegistrationBanner from "../components/MerchantRegistrationBanner";
import UserAvatar from "../components/UserAvatar";
import {READONLY_MERCHANT} from "../utils/env";
import {cn} from "../utils/style";
import {whiteLabelNameIs} from "../utils/whitelabel";
import MerchantSwitcher from "../components/MerchantSwitcher";

function DashboardLayout({children}) {
    const {token, authChecked, user, merchant, roles, permissions} = useAuthStore()
    const {t} = useTranslation()
    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => {
        if (authChecked && !token) {
            navigate("/login");
        }
    }, [token, authChecked]);

    const [sidebarOpen, setSidebarOpen] = useState(false)

    const navigation = [
        {name: t('navigation.dashboard'), href: '/dashboard', icon: HomeIcon, visible: true},
        {
            name: t('navigation.summary'),
            href: '/summary',
            icon: PresentationChartLineIcon,
            visible: !whiteLabelNameIs('TABEEB') && !permissions?.includes('sub_user_read_only')
        },
        // {name: t('navigation.registration'), href: '/merchant/registration', icon: DocumentCheckIcon},
        // {name: t('navigation.invoices'), href: '/merchant/invoices', icon: DocumentDuplicateIcon},
        {name: t('navigation.transactions'), href: '/merchant/payments', icon: CreditCardIcon, visible: true},
        // {name: t('navigation.subscriptions'), href: '/merchant/subscriptions', icon: BanknotesIcon},
        {
            name: t('navigation.settlements'),
            href: '/merchant/settlements',
            icon: CurrencyDollarIcon,
            visible: roles?.includes("merchant")
        },
        {
            name: t('navigation.refunds'),
            href: '/merchant/refunds',
            icon: CurrencyDollarIcon,
            visible: permissions?.includes("approve_refunds")
        },
    ]
    const merchant_navigation = [
        {id: 1, name: t('navigation.registration'), href: '/merchant/registration', initial: 'R', visible: true},
        // {id: 2, name: t('merchant_navigation.payment_gateway'), href: '/merchant/payment-gateway', initial: 'P', visible: !READONLY_MERCHANT},
        {
            id: 3,
            name: t('merchant_navigation.sub_users'),
            href: '/merchant/sub-users',
            initial: 'S',
            visible: !READONLY_MERCHANT
        },
        {
            id: 4,
            name: t('merchant_navigation.api_docs'),
            href: '/merchant/docs/v1',
            initial: 'A',
            visible: !READONLY_MERCHANT
        },
        {
            id: 5,
            name: t('merchant_navigation.sub_vendors'),
            href: '/merchant/sub-vendors',
            initial: 'V',
            visible: !READONLY_MERCHANT && !whiteLabelNameIs('TABEEB') && (merchant?.allowSubVendors === true)
        },
    ]
    const userNavigation = [
        {name: 'Your profile', href: '/profile'},
        {name: t('_.settings'), href: '/merchant/settings'},
        {name: 'Sign out', href: '/logout'},
    ]

    return (
        <div>
            <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-900/80"/>
                    </Transition.Child>

                    <div className="fixed inset-0 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                        <button type="button" className="-m-2.5 p-2.5"
                                                onClick={() => setSidebarOpen(false)}>
                                            <span className="sr-only">Close sidebar</span>
                                            <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true"/>
                                        </button>
                                    </div>
                                </Transition.Child>
                                <div
                                    className="flex grow flex-col gap-y-5 overflow-y-auto bg-white/80 backdrop-blur-xl px-6 pb-4">
                                    <div className="flex h-16 shrink-0 items-center">
                                        <Logo/>
                                    </div>
                                    <div className="px-1 -mt-2">
                                        <MerchantSwitcher className="w-full" />
                                    </div>
                                    <nav className="flex flex-1 flex-col">
                                        <ul className="flex flex-1 flex-col gap-y-7">
                                            <li>
                                                <ul className="-mx-2 space-y-1">
                                                    {navigation.map((item) => (
                                                        <li key={item.name}
                                                            className={clsx(!item?.visible ? 'hidden' : '')}>
                                                            <Link
                                                                to={item.href}
                                                                className={clsx(
                                                                    location.pathname === item.href
                                                                        ? 'bg-[#0b6a8c] shadow-lg shadow-blue-600/20 text-white scale-[1.02]'
                                                                        : 'text-gray-500 hover:text-[#0b6a8c] hover:bg-cyan-50/50 hover:scale-[1.02] transition-all duration-300',
                                                                    'group flex gap-x-3 rounded-2xl p-3 px-5 text-sm leading-6 font-bold items-center transform'
                                                                )}
                                                            >
                                                                <item.icon
                                                                    className={clsx(
                                                                        location.pathname === item.href ? 'text-white' : 'text-gray-400 group-hover:text-[#0b6a8c] transition-colors duration-300',
                                                                        'h-5 w-5 shrink-0'
                                                                    )}
                                                                    aria-hidden="true"
                                                                />
                                                                {item.name}
                                                                {location.pathname === item.href && (
                                                                    <span className="ml-auto flex h-2 w-2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                                                                )}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </li>
                                            {roles?.includes("merchant") &&
                                                <li className="mt-2">
                                                    <div
                                                        className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-4 mb-3">{t('merchant_navigation.section_title')}</div>
                                                    <ul className="-mx-2 space-y-1">
                                                        {merchant_navigation.map((item) => (
                                                            <li key={item.name}
                                                                className={cn(!item.visible ? 'hidden' : '')}>
                                                                <Link
                                                                    to={item.href}
                                                                    className={clsx(
                                                                        location.pathname === item.href
                                                                            ? 'bg-[#0b6a8c] shadow-lg shadow-blue-600/20 text-white scale-[1.02]'
                                                                            : 'text-gray-500 hover:text-[#0b6a8c] hover:bg-cyan-50/50 hover:scale-[1.02] transition-all duration-300',
                                                                        'group flex gap-x-3 rounded-2xl p-2.5 px-4 text-sm leading-6 font-semibold items-center transform'
                                                                    )}
                                                                >
																	<span
                                                                        className={clsx(
                                                                            location.pathname === item.href
                                                                                ? 'text-white border-white/20 bg-white/10'
                                                                                : 'text-gray-400 border-gray-100 group-hover:border-cyan-100 group-hover:bg-cyan-50/30 transition-all duration-300',
                                                                            'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border text-[0.625rem] font-bold bg-white'
                                                                        )}
                                                                    >
																		{item.initial}
																	</span>
                                                                    <span className="truncate">{item.name}</span>
                                                                    {location.pathname === item.href && (
                                                                        <span className="ml-auto flex h-1.5 w-1.5 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                                                                    )}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </li>}
                                            <li className="mt-auto">

                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col p-4">
                <div
                    className="flex grow flex-col gap-y-5 overflow-y-auto border border-gray-100 bg-white/80 backdrop-blur-xl rounded-[2.5rem] px-6 pb-4 shadow-sm">
                    <div className="flex h-16 shrink-0 items-center">
                        <Logo/>
                    </div>
                    <nav className="flex flex-1 flex-col">
                        <ul className="flex flex-1 flex-col gap-y-7">
                            <li>
                                    <ul className="-mx-2 space-y-1">
                                        {navigation.map((item) => (
                                            <li key={item.name}
                                                className={clsx(!item?.visible ? 'hidden' : '')}>
                                                <Link
                                                    to={item.href}
                                                    className={clsx(
                                                        location.pathname === item.href
                                                            ? 'bg-[#0b6a8c] shadow-lg shadow-blue-600/20 text-white scale-[1.02]'
                                                            : 'text-gray-500 hover:text-[#0b6a8c] hover:bg-cyan-50/50 hover:scale-[1.02] transition-all duration-300',
                                                        'group flex gap-x-3 rounded-2xl p-3 px-5 text-sm leading-6 font-bold items-center transform'
                                                    )}
                                                >
                                                    <item.icon
                                                        className={clsx(
                                                            location.pathname === item.href ? 'text-white' : 'text-gray-400 group-hover:text-[#0b6a8c] transition-colors duration-300',
                                                            'h-5 w-5 shrink-0'
                                                        )}
                                                        aria-hidden="true"
                                                    />
                                                    {item.name}
                                                    {location.pathname === item.href && (
                                                        <span className="ml-auto flex h-2 w-2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                                                    )}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                            </li>
                            {roles?.includes("merchant") &&
                                <li className="mt-2">
                                    <div
                                        className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-4 mb-3">{t('merchant_navigation.section_title')}</div>
                                    <ul className="-mx-2 space-y-1">
                                        {merchant_navigation.map((item) => (
                                            <li key={item.name} className={cn(!item.visible ? 'hidden' : '')}>
                                                <Link
                                                    to={item.href}
                                                    className={clsx(
                                                        location.pathname === item.href
                                                            ? 'bg-[#0b6a8c] shadow-lg shadow-blue-600/20 text-white scale-[1.02]'
                                                            : 'text-gray-500 hover:text-[#0b6a8c] hover:bg-cyan-50/50 hover:scale-[1.02] transition-all duration-300',
                                                        'group flex gap-x-3 rounded-2xl p-2.5 px-4 text-sm leading-6 font-semibold items-center transform'
                                                    )}
                                                >
													<span
                                                        className={clsx(
                                                            location.pathname === item.href
                                                                ? 'text-white border-white/20 bg-white/10'
                                                                : 'text-gray-400 border-gray-100 group-hover:border-cyan-100 group-hover:bg-cyan-50/30 transition-all duration-300',
                                                            'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border text-[0.625rem] font-bold bg-white'
                                                        )}
                                                    >
														{item.initial}
													</span>
                                                    <span className="truncate">{item.name}</span>
                                                    {location.pathname === item.href && (
                                                        <span className="ml-auto flex h-1.5 w-1.5 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                                                    )}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>}
                            <li className="mt-auto">
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            <div className="ltr:lg:pl-72 ltr:lg:pr-0 rtl:lg:pl-0 rtl:lg:pr-72 min-h-screen bg-gray-50/30">
                <div className="sticky top-0 z-40 w-full bg-white/80 backdrop-blur-xl lg:bg-transparent lg:backdrop-blur-none">
                    <div className="lg:mx-auto lg:max-w-7xl lg:px-8 2xl:px-0">
                        <div className="flex h-16 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-0">
                            <button
                                type="button"
                                className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
                                onClick={() => setSidebarOpen(true)}
                            >
                                <span className="sr-only">Open sidebar</span>
                                <Bars3Icon className="h-6 w-6" aria-hidden="true"/>
                            </button>

                            <div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true"/>

                            <div className="flex flex-1 items-center gap-x-4 self-stretch lg:gap-x-6">
                                <div className="flex flex-1">
                                    {/* Optional: Add a subtle search bar if needed in future */}
                                </div>
                                <div className="flex items-center gap-x-3 lg:gap-x-4 bg-white/95 backdrop-blur-lg p-1.5 px-4 rounded-full border border-gray-100 shadow-sm">
                                    <MerchantSwitcher />

                                    <div className="hidden sm:block w-px h-6 bg-gray-100 mx-1" aria-hidden="true"/>

                                    <div className="hidden sm:flex items-center bg-gray-100/50 rounded-full px-1 py-1 border border-gray-100/50">
                                        <Language />
                                    </div>

                                    <div className="hidden lg:block w-px h-6 bg-gray-100 mx-1" aria-hidden="true"/>

                                    <Menu as="div" className="relative">
                                        <Menu.Button className="group -m-1.5 flex items-center p-1.5 rounded-full hover:bg-gray-50 transition-all duration-200">
                                            <span className="sr-only">Open user menu</span>
                                            <div className="p-0.5 rounded-full border border-gray-100 group-hover:border-cyan-200 transition-colors">
                                                <UserAvatar className="h-8 w-8 rounded-full bg-gray-50 shadow-sm"/>
                                            </div>
                                            <span className="hidden lg:flex lg:items-center">
												<span
                                                    className="ltr:ml-3 rtl:mr-3 text-sm font-bold text-gray-700 group-hover:text-[#0b6a8c] transition-colors capitalize"
                                                    aria-hidden="true">
													{user?.name}
												</span>
												<ChevronDownIcon className="ltr:ml-1 rtl:mr-1 h-4 w-4 text-gray-400 group-hover:text-cyan-400 transition-colors"
                                                                 aria-hidden="true"/>
											</span>
                                        </Menu.Button>
                                        <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-100"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                        >
                                            <Menu.Items
                                                className="absolute right-0 z-10 mt-2.5 w-48 origin-top-right rounded-2xl bg-white p-2 shadow-xl ring-1 ring-gray-900/5 focus:outline-none border border-gray-50">
                                                {userNavigation.map((item) => (
                                                    <Menu.Item key={item.name}>
                                                        {({active}) => (
                                                            <Link
                                                                to={item.href}
                                                                className={clsx(
                                                                    active ? 'bg-cyan-50 text-[#0b6a8c]' : 'text-gray-700',
                                                                    'flex items-center px-4 py-2 text-sm font-bold rounded-xl transition-colors'
                                                                )}
                                                            >
                                                                {item.name}
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                ))}
                                            </Menu.Items>
                                        </Transition>
                                    </Menu>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {!merchant?.active && roles?.includes("merchant") && <MerchantRegistrationBanner/>}
                <main className="sm:py-4 sm:px-8 3xl:px-0">
                    <div
                        className="mx-auto max-w-7xl">
                        <div className="bg-white/50 backdrop-blur-md rounded-[3rem] p-4 sm:p-10 min-h-[calc(100vh-6rem)] border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                            {children}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    )
}

export default DashboardLayout;
